import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface FaqGroup {
  category: string;
  items: Faq[];
}

const UNCATEGORIZED = '__uncategorized__';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'null' && s !== 'undefined';
}

function groupByCategory(items: Faq[]): FaqGroup[] {
  const map = new Map<string, Faq[]>();
  const order: string[] = [];

  for (const item of items) {
    const rawCategory = hasValue(item.category) ? item.category!.trim() : UNCATEGORIZED;
    if (!map.has(rawCategory)) {
      map.set(rawCategory, []);
      order.push(rawCategory);
    }
    map.get(rawCategory)!.push(item);
  }

  return order.map((category) => ({
    category,
    items: map.get(category)!,
  }));
}

interface FaqAccordionItemProps {
  question: string;
  answer: string | null | undefined;
  defaultOpen: boolean;
}

function FaqAccordionItem({ question, answer, defaultOpen }: FaqAccordionItemProps) {
  const itemRef = useScrollReveal<HTMLDetailsElement>();
  const hasAnswer = hasValue(answer);

  return (
    <details ref={itemRef} className="reveal-fade-up faq-item" open={defaultOpen}>
      <summary className="faq-question">
        <span className="faq-question-text">{question}</span>
        <span className="faq-chevron" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </summary>
      <div className="faq-answer">
        {hasAnswer ? (
          <div
            className="faq-answer-body"
            dangerouslySetInnerHTML={{ __html: answer! }}
          />
        ) : (
          <p className="faq-answer-empty">No answer provided yet.</p>
        )}
      </div>
    </details>
  );
}

export function FaqList() {
  useRevealStyles();

  const faqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  const items: Faq[] = Array.isArray(faqs) ? faqs : [];
  const validItems = items.filter((faq) => hasValue(faq.question));
  const groups = groupByCategory(validItems);
  const hasMultipleCategories =
    groups.length > 1 ||
    (groups.length === 1 && groups[0].category !== UNCATEGORIZED);

  return (
    <section className="faq-section">
      <div className="faq-inner">
        <EditableCollectionBinding
          bindingPath="faqlist.items"
          label="FAQ Items"
        >
          {validItems.length > 0 ? (
            <div className="faq-groups">
              {groups.map((group, groupIndex) => {
                const showCategoryHeading =
                  hasMultipleCategories && group.category !== UNCATEGORIZED;
                return (
                  <div
                    key={group.category || groupIndex}
                    className="faq-group"
                  >
                    {showCategoryHeading && (
                      <h3 className="faq-group-heading">{group.category}</h3>
                    )}
                    <div className="faq-list">
                      {group.items.map((faq, itemIndex) => (
                        <FaqAccordionItem
                          key={`${group.category}-${itemIndex}-${faq.question}`}
                          question={faq.question || 'Question'}
                          answer={faq.answer}
                          defaultOpen={groupIndex === 0 && itemIndex === 0}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="faq-empty">
              <p>Frequently asked questions will appear here soon.</p>
            </div>
          )}
        </EditableCollectionBinding>
      </div>

      <style>{`
        .faq-section {
          background-color: var(--vibe-color-background);
          padding: 4rem 1.25rem 5rem;
        }
        @media (max-width: 640px) {
          .faq-section {
            padding: 2.75rem 1rem 3.5rem;
          }
        }
        .faq-inner {
          max-width: 880px;
          margin: 0 auto;
        }

        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }
        .faq-group {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 0.95rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--vibe-color-secondary);
          margin: 0 0 0.25rem;
          padding-bottom: 0.6rem;
          border-bottom: 1px solid var(--vibe-color-border);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
        }

        /* Accordion item */
        .faq-item {
          background: #ffffff;
          border: 1px solid var(--vibe-color-border);
          box-shadow: 0 2px 10px rgba(52, 46, 31, 0.05);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .faq-item:hover {
          box-shadow: 0 6px 18px rgba(52, 46, 31, 0.09);
          border-color: rgba(178, 140, 89, 0.4);
        }
        .faq-item[open] {
          border-color: var(--vibe-color-primary);
          box-shadow: 0 8px 24px rgba(52, 46, 31, 0.1);
        }

        .faq-question {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.1rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 1.02rem;
          font-weight: 600;
          color: var(--vibe-color-text);
          cursor: pointer;
          list-style: none;
          user-select: none;
        }
        .faq-question::-webkit-details-marker {
          display: none;
        }
        .faq-question:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: 2px;
        }
        .faq-question-text {
          flex: 1;
          min-width: 0;
          line-height: 1.4;
          text-align: left;
        }

        .faq-chevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--vibe-color-primary);
        }

        .faq-answer {
          padding: 0 1.25rem 1.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.98rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
        }
        .faq-answer-body {
          /* Keep rich answer HTML readable with consistent spacing. */
        }
        .faq-answer-body p {
          margin: 0 0 0.75rem;
        }
        .faq-answer-body p:last-child {
          margin-bottom: 0;
        }
        .faq-answer-body ul,
        .faq-answer-body ol {
          margin: 0 0 0.75rem;
          padding-left: 1.25rem;
        }
        .faq-answer-body ul {
          list-style: disc;
        }
        .faq-answer-body ol {
          list-style: decimal;
        }
        .faq-answer-body li {
          margin-bottom: 0.25rem;
        }
        .faq-answer-body a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }
        .faq-answer-body strong {
          color: var(--vibe-color-text);
        }
        .faq-answer-empty {
          margin: 0;
          font-style: italic;
          opacity: 0.75;
        }

        .faq-empty {
          text-align: center;
          padding: 3rem 1rem;
          color: var(--vibe-color-text-muted);
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          background: #ffffff;
          border: 1px dashed var(--vibe-color-border);
        }

        @media (max-width: 640px) {
          .faq-group-heading {
            text-align: center;
          }
          .faq-question {
            padding: 0.95rem 1rem;
            font-size: 0.98rem;
          }
          .faq-answer {
            padding: 0 1rem 1.1rem;
            font-size: 0.95rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FaqList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQ Items',
      collection: 'faqs',
    },
  },
};

(FaqList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FaqList;
